import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { submitHohDemo } from "../lib/api";
import { trackFormAction } from "../lib/tracker";
import useRevealWhen from "../hooks/useRevealWhen";

// Book a Demo: the same questions as the Heart of House app's /request-demo form (only name and email
// are required), next to the Interactive Demo card.
const ROLES = ["owner", "gm", "chef", "sous_chef", "bar_manager", "director", "other"];
const TIERS = ["core", "pro", "intelligence", "unsure"];
const POS_SYSTEMS = ["square", "toast", "clover", "lightspeed", "micros", "genius", "touchbistro", "no_pos", "other"];
const REFERRALS = [
  "google", "instagram", "facebook", "linkedin", "restaurant_or_chef", "supplier", "startup_tnt_or_event", "news", "other",
];
const POS_LABELS = {
  square: "Square", toast: "Toast", clover: "Clover", lightspeed: "Lightspeed", micros: "Oracle MICROS",
  genius: "Genius", touchbistro: "TouchBistro",
};
const SUPPORT_EMAIL = "support@misekitchen.ca";
const HOH_LOGO = "/images/logos/heart-of-house.png";
const HOH_APP_URL = "https://heartofhouse.ca";

const initialForm = {
  name: "",
  email: "",
  restaurant_name: "",
  role: "",
  tier: "",
  pos: "",
  pos_other: "",
  pain_point: "",
  availability: "",
  referral: "",
  referral_other: "",
};
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputClass =
  "w-full bg-[#161616] border border-[#3a3a3a] hover:border-[#555555] focus:border-[#E8490F] focus:ring-2 focus:ring-[#E8490F]/20 outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30 transition-colors aria-[invalid=true]:border-red-400/70";

const HeartOfHouseDemo = () => {
  const { t } = useLanguage();
  const d = t.hoh.demo;
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const successRef = useRevealWhen(Boolean(sentTo));

  useEffect(() => {
    const prevTitle = document.title;
    document.title = d.metaTitle;
    return () => {
      document.title = prevTitle;
    };
  }, [d.metaTitle]);

  const change = (name, value) => {
    if (form.name === "" && form.email === "") trackFormAction("hoh-demo:start");
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: null }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const errs = {};
    if (!form.name.trim()) errs.name = d.required.name;
    if (!EMAIL_SHAPE.test(form.email.trim())) errs.email = d.required.email;
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSubmitting(true);
    try {
      const email = form.email.trim();
      await submitHohDemo({
        name: form.name.trim(),
        email,
        restaurant_name: form.restaurant_name.trim() || null,
        role: form.role || null,
        tier: form.tier || null,
        pos: form.pos || null,
        pos_other: form.pos === "other" ? form.pos_other.trim() || null : null,
        pain_point: form.pain_point.trim() || null,
        availability: form.availability.trim() || null,
        referral: form.referral || null,
        referral_other: form.referral === "other" ? form.referral_other.trim() || null : null,
      });
      trackFormAction("hoh-demo:submit");
      setSentTo(email);
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      toast.error(d.errorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  const posOptions = POS_SYSTEMS.map((v) => [v, POS_LABELS[v] || t.hoh.pilotForm.posOptions[v]]);
  const [beforeEmail, afterEmail] = d.successBody.split("{email}");

  return (
    <div data-testid="page-hoh-demo">
      <section className="relative overflow-hidden">
        {/* backdrop: two soft glows over a faint dot grid */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.35] [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:28px_28px] [mask-image:linear-gradient(to_bottom,black,transparent_75%)]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -top-40 -left-32 h-[520px] w-[520px] rounded-full bg-[#E8490F]/15 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute top-[40%] -right-40 h-[480px] w-[480px] rounded-full bg-[#E8490F]/10 blur-3xl"
          aria-hidden
        />

        <div className="mise-container relative pt-14 md:pt-20 pb-20 md:pb-28">
          <Link
            to="/heart-of-house"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
            data-testid="hoh-demo-back"
          >
            <ArrowLeft size={14} />
            {t.hoh.pilotForm.returnCta}
          </Link>

          <div className="mt-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
            {/* LEFT: the pitch */}
            <div className="lg:col-span-5 min-w-0">
              <div className="lg:sticky lg:top-28">
                <a
                  href={HOH_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block opacity-95 hover:opacity-100 transition-opacity"
                  data-track="hoh-demo-logo"
                >
                  <img src={HOH_LOGO} alt={t.hoh.heading} className="h-16 md:h-20 w-auto" />
                </a>
                <p className="mt-10 mise-eyebrow">{d.eyebrow}</p>
                <h1 className="mt-4 text-5xl md:text-7xl font-semibold tracking-tight leading-[0.98]">{d.heading}</h1>
                <p className="mt-5 mise-serif mise-accent text-3xl md:text-4xl leading-tight">{d.subheading}</p>
                <p className="mt-6 text-lg text-white/70 leading-relaxed">{d.intro}</p>

                <ol className="mt-10 relative" data-testid="hoh-demo-steps">
                  <span className="absolute left-[15px] top-4 bottom-4 w-px bg-gradient-to-b from-[#E8490F] via-[#E8490F]/40 to-transparent" aria-hidden />
                  {d.steps.map((step, i) => (
                    <li key={step.title} className="relative flex gap-5 pb-7 last:pb-0">
                      <span className="relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-[#E8490F]/60 bg-[#111111] text-sm font-semibold text-[#E8490F]">
                        {i + 1}
                      </span>
                      <div>
                        <p className="font-semibold text-white">{step.title}</p>
                        <p className="mt-1 text-white/60 leading-relaxed">{step.text}</p>
                      </div>
                    </li>
                  ))}
                </ol>

                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  data-track="hoh-demo-email-support"
                  className="mt-10 inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
                >
                  <Mail size={15} className="text-[#E8490F]" />
                  {d.orEmail} <span className="text-white underline decoration-[#E8490F]/60 underline-offset-4">{SUPPORT_EMAIL}</span>
                </a>
              </div>
            </div>

            {/* RIGHT: the form, in a card with a glowing edge */}
            <div className="lg:col-span-7 min-w-0">
              <div className="rounded-[1.35rem] bg-gradient-to-br from-[#E8490F]/70 via-[#444444]/60 to-[#242424] p-px shadow-[0_40px_80px_-30px_rgba(232,73,15,0.35)]">
                <div className="rounded-[1.3rem] bg-[#1c1c1c] p-6 sm:p-8 md:p-10">
                  {sentTo ? (
                    <div
                      className="py-10 text-center outline-none"
                      ref={successRef}
                      tabIndex={-1}
                      role="status"
                      data-testid="hoh-demo-success"
                    >
                      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#E8490F]/15">
                        <CheckCircle2 className="text-[#E8490F]" size={34} />
                      </span>
                      <h2 className="mt-6 text-3xl font-semibold text-white">{d.successTitle}</h2>
                      <p className="mt-3 text-lg text-white/70 leading-relaxed">
                        {beforeEmail}
                        <span className="text-white font-medium">{sentTo}</span>
                        {afterEmail}
                      </p>
                      <button
                        type="button"
                        onClick={() => setSentTo(null)}
                        className="mt-8 mise-pill-secondary"
                        data-testid="hoh-demo-another"
                      >
                        {d.another}
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={onSubmit} noValidate className="grid gap-5" data-testid="hoh-demo-form">
                      <h2 className="text-2xl md:text-3xl font-semibold text-white tracking-tight">{d.formHeading}</h2>
                      <Row>
                        <TextField id="demo-name" label={d.labels.name} required value={form.name} autoComplete="name"
                          onChange={(v) => change("name", v)} error={errors.name} />
                        <TextField id="demo-email" type="email" label={d.labels.email} required value={form.email}
                          autoComplete="email" onChange={(v) => change("email", v)} error={errors.email} />
                      </Row>
                      <Row>
                        <TextField id="demo-restaurant" label={d.labels.restaurant} value={form.restaurant_name}
                          autoComplete="organization" onChange={(v) => change("restaurant_name", v)} />
                        <SelectField id="demo-role" label={d.labels.role} value={form.role} placeholder={d.pickOne}
                          options={ROLES.map((v) => [v, d.roles[v]])} onChange={(v) => change("role", v)} />
                      </Row>

                      <fieldset>
                        <legend className="text-sm text-white/60 mb-2">{d.labels.tier}</legend>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="demo-tier">
                          {TIERS.map((v) => {
                            const on = form.tier === v;
                            return (
                              <button
                                key={v}
                                type="button"
                                aria-pressed={on}
                                onClick={() => change("tier", on ? "" : v)}
                                className={`rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors ${
                                  on
                                    ? "border-[#E8490F] bg-[#E8490F] text-white"
                                    : "border-[#3a3a3a] bg-[#161616] text-white/75 hover:border-[#555555] hover:text-white"
                                }`}
                                data-testid={`demo-tier-${v}`}
                              >
                                {d.tiers[v]}
                              </button>
                            );
                          })}
                        </div>
                      </fieldset>

                      <Row>
                        <SelectField id="demo-pos" label={d.labels.pos} value={form.pos} placeholder={d.pickOne}
                          options={posOptions} onChange={(v) => change("pos", v)}
                          other={form.pos === "other" && (
                            <OtherInput label={d.labels.posOther} value={form.pos_other}
                              onChange={(v) => change("pos_other", v)} testId="demo-pos-other" />
                          )} />
                        <SelectField id="demo-referral" label={d.labels.referral} value={form.referral}
                          placeholder={d.pickOne} options={REFERRALS.map((v) => [v, d.referralOptions[v]])}
                          onChange={(v) => change("referral", v)}
                          other={form.referral === "other" && (
                            <OtherInput label={d.labels.referralOther} value={form.referral_other}
                              onChange={(v) => change("referral_other", v)} testId="demo-referral-other" />
                          )} />
                      </Row>

                      <div className="grid gap-2">
                        <label className="text-sm text-white/60" htmlFor="demo-pain">{d.labels.pain}</label>
                        <textarea id="demo-pain" rows={3} maxLength={4000} value={form.pain_point}
                          placeholder={d.placeholders.pain} onChange={(e) => change("pain_point", e.target.value)}
                          className={`${inputClass} leading-relaxed`} data-testid="demo-pain" />
                      </div>
                      <div className="grid gap-2">
                        <label className="text-sm text-white/60" htmlFor="demo-availability">{d.labels.availability}</label>
                        <input id="demo-availability" value={form.availability} maxLength={1000}
                          placeholder={d.placeholders.availability}
                          onChange={(e) => change("availability", e.target.value)} className={inputClass}
                          data-testid="demo-availability" />
                        <p className="text-xs text-white/40">{d.availabilityNote}</p>
                      </div>

                      <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
                        <button
                          type="submit"
                          disabled={submitting}
                          className="mise-pill-primary justify-center shadow-[0_12px_30px_-10px_rgba(232,73,15,0.7)] disabled:opacity-60 disabled:cursor-not-allowed"
                          data-testid="hoh-demo-submit"
                        >
                          {submitting ? d.sending : d.submit}
                          {!submitting && <ArrowRight size={16} />}
                        </button>
                        <p className="inline-flex items-center gap-2 text-sm text-white/55">
                          <Check size={15} className="text-[#E8490F]" />
                          {d.promise}
                        </p>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTERACTIVE DEMO: COMING IN NOVEMBER */}
      <section className="pb-20 md:pb-28">
        <div className="mise-container">
          <div
            className="relative overflow-hidden rounded-[1.75rem] border border-[#E8490F]/30 bg-gradient-to-br from-[#2b1810] via-[#1c1c1c] to-[#141414] p-8 sm:p-10 md:p-14"
            data-testid="hoh-demo-interactive"
          >
            <div
              className="pointer-events-none absolute -top-24 -right-24 h-80 w-80 rounded-full bg-[#E8490F]/25 blur-3xl"
              aria-hidden
            />
            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-6 min-w-0">
                <span className="inline-flex items-center gap-2 rounded-full border border-[#E8490F]/40 bg-[#E8490F]/10 px-4 py-1.5 text-[11px] font-semibold tracking-[0.2em] uppercase text-[#E8490F]">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#E8490F] opacity-60" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#E8490F]" />
                  </span>
                  {d.interactiveBadge}
                </span>
                <h2 className="mt-6 text-4xl md:text-5xl font-semibold tracking-tight text-white leading-[1.05]">
                  {d.interactiveTitle}
                </h2>
                <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">{d.interactiveText}</p>
              </div>
              <div className="lg:col-span-6 min-w-0 flex justify-center lg:justify-end">
                {/* the sneak-peek clip is portrait, so it sits in a phone-shaped frame */}
                <figure className="mx-auto w-full max-w-[260px] lg:mr-6">
                  <figcaption className="mb-3 text-center text-[11px] font-semibold tracking-[0.2em] uppercase text-white/50">
                    {d.sneakPeek}
                  </figcaption>
                  <div className="overflow-hidden rounded-[2rem] border-[6px] border-[#2a2a2a] bg-[#0d0d0d] shadow-[0_40px_80px_-30px_rgba(232,73,15,0.45)] ring-1 ring-[#444444]">
                    <video controls playsInline preload="metadata" className="block w-full h-auto" data-testid="hoh-demo-video">
                      <source src="/videos/hoh-demo.mp4#t=0.5" type="video/mp4" />
                    </video>
                  </div>
                </figure>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const Row = ({ children }) => <div className="grid sm:grid-cols-2 gap-5">{children}</div>;

const TextField = ({ id, label, value, onChange, error, required, type = "text", autoComplete }) => (
  <div className="grid gap-2 content-start min-w-0">
    <label className="text-sm text-white/60" htmlFor={id}>
      {label}
      {required && <span className="text-[#E8490F]"> *</span>}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      required={required}
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
      aria-invalid={Boolean(error)}
      aria-describedby={error ? `${id}-error` : undefined}
      className={inputClass}
      data-testid={id}
    />
    {error && (
      <p id={`${id}-error`} className="text-xs text-red-300" role="alert">
        {error}
      </p>
    )}
  </div>
);

const SelectField = ({ id, label, value, onChange, placeholder, options, other }) => (
  <div className="grid gap-2 content-start min-w-0">
    <label className="text-sm text-white/60" htmlFor={id}>
      {label}
    </label>
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${inputClass} ${value ? "" : "text-white/40"}`}
      data-testid={id}
    >
      <option value="">{placeholder}</option>
      {options.map(([v, text]) => (
        <option key={v} value={v} className="text-white">
          {text}
        </option>
      ))}
    </select>
    {other}
  </div>
);

const OtherInput = ({ label, value, onChange, testId }) => (
  <input
    aria-label={label}
    placeholder={label}
    maxLength={200}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={inputClass}
    data-testid={testId}
  />
);

export default HeartOfHouseDemo;
